"use client";

import { GuideCtaButton } from "@/components/marketing/guide-start-cta";

/**
 * "Use this in The Site Book" — the post-example ask on guides that display a
 * worked example document (see GUIDES_WITH_EXAMPLE_BLOCKS in
 * lib/guides/cta-copy.ts). Placed directly after the example content, before
 * the next section: the reader has just seen what good output looks like, so
 * the ask bridges from the example to their own job.
 *
 * Copy rules (lib/guides/cta-copy.ts header): no speed figures — the
 * originally requested specific completion duration is a time claim with no
 * admissible evidence, so the card states what IS verifiable: the samples are
 * genuine product output for the fictional demo project (the phrasing already
 * published on /guides/construction-phase-plan-example).
 */
export function GuideExampleCta({ slug }: { slug: string }) {
  return (
    <div
      data-guide-cta="post-example"
      className="mt-8 rounded-xl border-2 border-attn-line bg-attn-bg p-6"
    >
      <h3 className="text-lg font-bold text-attn">Use this in The Site Book</h3>
      <p className="mt-1 text-sm leading-relaxed text-attn">
        The worked example above shows the shape. The sample documents are
        genuine The Site Book output, generated for the fictional demo project
        used across our samples — create yours from your job details.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <GuideCtaButton
          slug={slug}
          position="post-example"
          ctaCopy="Create mine"
          label="Create mine →"
          className="inline-flex min-h-[44px] items-center justify-center rounded-[10px] bg-accent px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
        />
        <GuideCtaButton
          slug={slug}
          position="post-example"
          ctaCopy="See a live example"
          label="See a live example →"
          href="/samples"
          className="inline-flex min-h-[44px] items-center justify-center rounded-[10px] border border-accent/40 bg-white px-5 py-2.5 text-sm font-bold text-brand-700 transition-colors hover:bg-accent-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
        />
      </div>
    </div>
  );
}
